import React, { useState } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
// Importaciones de componentes
//Login
import LoginPage from './pages/LoginPage';
//layouts
import MainLayout from './layouts/MainLayout';
//Dashboard
import DashboardPage from './pages/Dashboard/DashboardPage';
//Users
import UsersPage from './pages/Clients/UsersPage';
import AddCliente from './pages/Clients/AddCliente/AddCliente';
//Proyects
import Proyects from './pages/Proyects/Proyects';
import AddProyect from './pages/Proyects/AddProyect/AddProyect';
//ProyectManager
import ProyectManager from './pages/ProyectManager/ProyectManager';
import AddProyectManager from './pages/ProyectManager/AddProyectManager/AddProyectManager';
//Support
import Support from './pages/Support/Support';
import Ticket from './pages/Support/Ticket/Ticket';
//AdminUsuarios
import AdminUsuariosPage from './pages/AdminUsuarios/AdminUsuariosPage';
//AdminPlanes
import AdminPlanesPage from './pages/AdminPlanes/AdminPlanesPage';
import AdminAsistentePage from './pages/AdminAsistente/AdminAsistentePage';
//Profile
import ProfilePage from './pages/Profile/ProfilePage';
//AdminCuentas
import AdminCuentasPage from './pages/AdminCuentas/AdminCuentasPage';
import AdminEquipoPage from './pages/AdminEquipo/AdminEquipoPage';
//AdminPrecios / AdminCostos
import AdminPreciosPage from './pages/AdminPrecios/AdminPreciosPage';
import AgentTypesPage from './pages/AgentTypes/AgentTypesPage';
import AgentsPage from './pages/Agents/AgentsPage';
import AgentDetailPage from './pages/Agents/AgentDetailPage';
import IntegrationsPage from './pages/Integrations/IntegrationsPage';
import ToolsPage from './pages/Tools/ToolsPage';
import ToolEditorPage from './pages/Tools/ToolEditorPage';
import AdminCostosPage from './pages/AdminCostos/AdminCostosPage';
// Importaciones de React Toastify
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
// Importaciones de SweetAlert2
import 'sweetalert2/dist/sweetalert2.min.css';
// Estilos personalizados para SweetAlert2
import './styles/sweetalert2-custom.css';
// Estilos personalizados para Toastify
import './styles/toastify-custom.css';

function App() {
  return (
    <MotionConfig reducedMotion="user">
      <AuthProvider>
        <Router>
          <ThemeProvider>
            <AppContent />
          </ThemeProvider>
        </Router>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
        />
      </AuthProvider>
    </MotionConfig>
  );
}

// Protege rutas que requieren autenticación y rol admin. Va FUERA de AppContent: definido adentro, cada render crearía un componente
// distinto y React desmontaría y volvería a montar todo el layout y la pantalla (se perdía lo escrito y parecía un reinicio).
// `allow`: los roles que pueden entrar (admin por defecto). Un empleado que llega a una pantalla de admin vuelve a Agentes.
const ProtectedRoute = ({ children, allow = ['admin'] }) => {
  const { isAuthenticated, role } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!allow.includes(role)) return <Navigate to={role === 'employee' ? '/agentes' : '/login'} replace />;
  return children;
};
const STAFF = ['admin', 'employee'];
const homeFor = (role) => (role === 'employee' ? '/agentes' : '/dashboard');

// Separate component to use the auth hook inside the AuthProvider
const AppContent = () => {
  const { isAuthenticated, role } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={(isAuthenticated && STAFF.includes(role)) ? <Navigate to={homeFor(role)} replace /> : <LoginPage />} />
      <Route path="/" element={
        <ProtectedRoute allow={STAFF}>
          <MainLayout/>
        </ProtectedRoute>
      }>
        {/* Sin esto, entrar a «/» dejaba el armazón con el contenido vacío (pantalla en blanco). */}
        <Route index element={<Navigate to={homeFor(role)} replace />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardPage/>
          </ProtectedRoute>
        } />
        <Route path="/clients" element={
          <ProtectedRoute>
            <UsersPage/>
          </ProtectedRoute>
        } />
        <Route path="/clients/add" element={
          <ProtectedRoute>
            <AddCliente/>
          </ProtectedRoute>
        } />
        <Route path="/clients/edit" element={
          <ProtectedRoute>
            <AddCliente/>
          </ProtectedRoute>
        } />
        <Route path="/proyects" element={
          <ProtectedRoute allow={STAFF}>
            <Proyects/>
          </ProtectedRoute>
        } />
        <Route path="/proyects/add" element={
          <ProtectedRoute>
            <AddProyect/>
          </ProtectedRoute>
        } />
        <Route path="/proyects/edit" element={
          <ProtectedRoute>
            <AddProyect/>
          </ProtectedRoute>
        } />
        <Route path="/proyect_manager" element={
          <ProtectedRoute>
            <ProyectManager/>
          </ProtectedRoute>
        } />
        <Route path="/proyect_manager/add" element={
          <ProtectedRoute>
            <AddProyectManager/>
          </ProtectedRoute>
        } />
        <Route path="/support" element={
          <ProtectedRoute>
            <Support/>
          </ProtectedRoute>
        } />
        <Route path="/support/ticket" element={
          <ProtectedRoute>
            <Ticket/>
          </ProtectedRoute>
        } />
        <Route path="/admin-usuarios" element={
          <ProtectedRoute>
            <AdminUsuariosPage/>
          </ProtectedRoute>
        } />
        <Route path="/admin-planes" element={
          <ProtectedRoute>
            <AdminPlanesPage/>
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute allow={STAFF}>
            <ProfilePage/>
          </ProtectedRoute>
        } />
        <Route path="/admin-cuentas" element={
          <ProtectedRoute>
            <AdminCuentasPage/>
          </ProtectedRoute>
        } />
        <Route path="/admin-equipo" element={
          <ProtectedRoute>
            <AdminEquipoPage/>
          </ProtectedRoute>
        } />
        <Route path="/agentes" element={
          <ProtectedRoute allow={STAFF}>
            <AgentsPage />
          </ProtectedRoute>
        } />
        <Route path="/agentes/:agentId/:section?" element={
          <ProtectedRoute allow={STAFF}>
            <AgentDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/integraciones" element={
          <ProtectedRoute allow={STAFF}>
            <IntegrationsPage />
          </ProtectedRoute>
        } />
        <Route path="/herramientas" element={
          <ProtectedRoute allow={STAFF}>
            <ToolsPage />
          </ProtectedRoute>
        } />
        <Route path="/herramientas/:toolId" element={
          <ProtectedRoute allow={STAFF}>
            <ToolEditorPage />
          </ProtectedRoute>
        } />
        <Route path="/agent-types" element={
          <ProtectedRoute allow={STAFF}>
            <AgentTypesPage/>
          </ProtectedRoute>
        } />
        <Route path="/admin-precios" element={
          <ProtectedRoute>
            <AdminPreciosPage/>
          </ProtectedRoute>
        } />
        <Route path="/admin-asistente" element={
          <ProtectedRoute>
            <AdminAsistentePage/>
          </ProtectedRoute>
        } />
        <Route path="/admin-costos" element={
          <ProtectedRoute>
            <AdminCostosPage/>
          </ProtectedRoute>
        } />
      </Route>
      {/* Una dirección que no existe lleva al inicio (si no hay sesión, de ahí al login) en vez de quedar en blanco. */}
      <Route path="*" element={<Navigate to={homeFor(role)} replace />} />
    </Routes>
  );
};

export default App;
